/**
 * Notification Context
 * Manages notification state across the application
 * - Unread count for header badge
 * - Notification list for dropdown/page
 * - Mark as read functionality
 * - Real-time updates via WebSocket
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from '../lib/axios'; // Use configured axios with CSRF
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { lastNotification } = useWebSocket() || {};
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 20;
  
  // Track processed notification IDs to prevent duplicates
  const processedNotificationIds = useRef(new Set());

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`, {
        params: { skip: (pageNum - 1) * ITEMS_PER_PAGE, limit: ITEMS_PER_PAGE }
      });
      
      const newNotifications = response.data.notifications || response.data || [];
      
      if (append) {
        setNotifications(prev => [...prev, ...newNotifications]);
      } else {
        setNotifications(newNotifications);
      }
      
      setHasMore(newNotifications.length === ITEMS_PER_PAGE);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/notifications/unread/count`);
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      // Silent fail - don't disrupt UX
    }
  }, [user]);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  // Delete a notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, [notifications]);

  // Load more notifications (pagination)
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchNotifications(page + 1, true);
    }
  }, [loading, hasMore, page, fetchNotifications]);

  // Refresh notifications (pull to refresh / manual refresh)
  const refresh = useCallback(() => {
    setPage(1);
    setHasMore(true);
    fetchNotifications(1, false);
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  // Add new notification with deduplication check (called from WebSocket)
  const addNotification = useCallback((notification) => {
    // Deduplication check - prevent adding the same notification twice
    if (!notification?.id) return;
    
    setNotifications(prev => {
      // Check if notification already exists in the list
      if (prev.some(n => n.id === notification.id)) {
        return prev; // Don't add duplicate
      }
      return [notification, ...prev];
    });
    
    if (!notification.read) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  // Listen to WebSocket lastNotification for real-time updates
  useEffect(() => {
    if (!lastNotification?.id) return;
    
    // Check if we've already processed this notification
    if (processedNotificationIds.current.has(lastNotification.id)) {
      return;
    }
    
    // Mark as processed
    processedNotificationIds.current.add(lastNotification.id);
    
    // Add to notifications list
    addNotification(lastNotification);
    
    // Clean up old processed IDs to prevent memory leak (keep last 100)
    if (processedNotificationIds.current.size > 100) {
      const idsArray = Array.from(processedNotificationIds.current);
      processedNotificationIds.current = new Set(idsArray.slice(-50));
    }
  }, [lastNotification, addNotification]);

  // Initial fetch when user logs in
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      fetchNotifications(1, false);
    } else {
      // Clear state on logout
      setNotifications([]);
      setUnreadCount(0);
      setPage(1);
      setHasMore(true);
    }
  }, [user, fetchNotifications, fetchUnreadCount]);

  // Poll for unread count every 60 seconds (backup for WebSocket)
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      hasMore,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      loadMore,
      refresh,
      addNotification,
      fetchUnreadCount
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
